import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Shield, Smartphone, Mail, Loader2, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

const TwoFactorSettings = () => {
  const [loading, setLoading] = useState(true);
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  
  // TOTP Setup state
  const [showTotpSetup, setShowTotpSetup] = useState(false);
  const [totpSetupData, setTotpSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  // Disable confirmation
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disableType, setDisableType] = useState(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await API.get('/auth/2fa/status');
      setTotpEnabled(response.data.totp_enabled);
      setEmailEnabled(response.data.email_2fa_enabled);
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
      toast.error('Failed to load 2FA settings');
    } finally {
      setLoading(false);
    }
  };

  const startTotpSetup = async () => {
    try {
      setLoading(true);
      const response = await API.post('/auth/2fa/totp/setup');
      setTotpSetupData(response.data);
      setShowTotpSetup(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start setup');
    } finally {
      setLoading(false);
    }
  };

  const verifyTotpSetup = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setVerifying(true);
    try {
      await API.post('/auth/2fa/totp/verify', { code: verificationCode });
      setTotpEnabled(true);
      setShowTotpSetup(false);
      setTotpSetupData(null);
      setVerificationCode('');
      toast.success('Authenticator app enabled!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setVerifying(false);
    }
  };

  const toggleEmailTwoFactor = async (enabled) => {
    if (enabled) {
      try {
        setLoading(true);
        await API.post('/auth/2fa/email/enable');
        setEmailEnabled(true);
        toast.success('Email 2FA enabled');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to enable email 2FA');
      } finally {
        setLoading(false);
      }
    } else {
      setDisableType('email');
      setShowDisableDialog(true);
    }
  };

  const confirmDisable = async () => {
    if (!disablePassword) {
      toast.error('Password required');
      return;
    }
    
    setDisabling(true);
    try {
      const endpoint = disableType === 'totp' 
        ? '/auth/2fa/totp/disable' 
        : '/auth/2fa/email/disable';
      
      await API.post(endpoint, { password: disablePassword });
      
      if (disableType === 'totp') {
        setTotpEnabled(false);
      } else {
        setEmailEnabled(false);
      }
      
      toast.success(`${disableType === 'totp' ? 'Authenticator' : 'Email'} 2FA disabled`);
      setShowDisableDialog(false);
      setDisablePassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setDisabling(false);
    }
  };

  if (loading && !showTotpSetup) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Authenticator App */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Smartphone className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="font-medium">Authenticator App</div>
                <div className="text-sm text-gray-500">
                  Use Google Authenticator, Authy, or similar
                </div>
              </div>
            </div>
            {totpEnabled ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <Check className="h-4 w-4" /> Enabled
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setDisableType('totp');
                    setShowDisableDialog(true);
                  }}
                >
                  Disable
                </Button>
              </div>
            ) : (
              <Button onClick={startTotpSetup} disabled={loading}>
                Set Up
              </Button>
            )}
          </div>
          
          {/* Email 2FA */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="font-medium">Email Verification</div>
                <div className="text-sm text-gray-500">
                  Receive a code via email when logging in
                </div>
              </div>
            </div>
            <Switch
              checked={emailEnabled}
              onCheckedChange={toggleEmailTwoFactor}
              disabled={loading}
            />
          </div>
          
          {/* Info */}
          {!totpEnabled && !emailEnabled && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Recommendation:</strong> Enable at least one form of 2FA to protect 
                your account from unauthorized access.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* TOTP Setup Dialog */}
      <Dialog open={showTotpSetup} onOpenChange={setShowTotpSetup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set Up Authenticator App</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app
            </DialogDescription>
          </DialogHeader>
          
          {totpSetupData && (
            <div className="space-y-4">
              {/* QR Code */}
              <div className="flex justify-center p-4 bg-white rounded-lg border">
                <img 
                  src={totpSetupData.qr_code} 
                  alt="TOTP QR Code" 
                  className="w-48 h-48"
                />
              </div>
              
              {/* Manual entry */}
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-1">Or enter this code manually:</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                  {totpSetupData.secret}
                </code>
              </div>
              
              {/* Verification */}
              <div className="space-y-2">
                <Label htmlFor="totp-code">Enter code from app</Label>
                <Input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-widest"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTotpSetup(false)}>
              Cancel
            </Button>
            <Button onClick={verifyTotpSetup} disabled={verifying || verificationCode.length !== 6}>
              {verifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Verify & Enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Confirmation Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter your password to confirm disabling {disableType === 'totp' ? 'authenticator' : 'email'} 2FA
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="disable-password">Password</Label>
              <Input
                id="disable-password"
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="Enter your password"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDisableDialog(false);
              setDisablePassword('');
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDisable} 
              disabled={disabling || !disablePassword}
            >
              {disabling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Disable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TwoFactorSettings;
